import { DEFAULT_CURRENCY, DEFAULT_LOCALE, IntlFormat } from './constants';
import { LocaleType } from './locale.types';
/**
 * Note that this file should be treated as internal machinery for the functions exported by the index
 */

// Quick alias for reuse below
type UnitDisplayType = 'short' | 'long' | 'narrow';

/**
 * getValidIntlLocale
 *
 * Ensures a given locale is supported by the Intl Format and provides a fallback value
 *
 * @param locale A single locale or array of locales to use in finding the best match
 * @param intlFormat Which Intl Format to use
 */
export const getValidIntlLocale = (
  locale: LocaleType = DEFAULT_LOCALE,
  intlFormat: IntlFormat = IntlFormat.NumberFormat,
): string => {
  let validLocale = Array.isArray(locale) ? locale[0] : locale;

  try {
    // the Intl constructor includes the `supportedLocalesOf` static method,
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/supportedValuesOf
    // which is passed down to all Intl constructors. Typescript doesn't like invoking it directly,
    // and so we have to cast it to one of the Intl constructor types to avoid type errors.
    // but _all_ of them have the `supportedLocalesOf` static method available, so this is a safe casting
    // to avoid the type error. Plus this is in a `try` block, so if it fails, we stop any errors from happening to begin with
    [validLocale] = (Intl[intlFormat] as Intl.DateTimeFormatConstructor).supportedLocalesOf(locale);
  } catch (err) {
    // do nothing
  }

  return validLocale || DEFAULT_LOCALE;
};

// NumberFormats

/**
 * getNumberFormat
 *
 * Formats number, using decimals only if necessary.
 *
 * @param locale A single locale or array of locales to use in finding the best match
 *
 * @example: en-US, 1432.95 => 1,432.95
 * @example: en-US 5280.00 => 5,280
 * @example: en-US 987654321 => 987,654,321
 */
export const getNumberFormat = (locale: LocaleType = DEFAULT_LOCALE) => {
  const formatLocale = getValidIntlLocale(locale);

  return new Intl.NumberFormat(formatLocale);
};

/**
 * getCompactNumberFormat
 *
 * Formats large numbers using shorthand units
 *
 * @param locale A single locale or array of locales to use in finding the best match
 *
 * @example: en-US, 987654321 => 988M (vs 987,654,321 using getNumberFormat)
 * @example: en-US, 1432.95 => 1k
 */
export const getCompactNumberFormat = (
  locale: LocaleType = DEFAULT_LOCALE,
  options?: Intl.NumberFormatOptions,
) => {
  const formatLocale = getValidIntlLocale(locale, IntlFormat.NumberFormat);
  const defaultOptions: Intl.NumberFormatOptions = { notation: 'compact', compactDisplay: 'short' };
  const formatOptions = { ...defaultOptions, ...options };
  return new Intl.NumberFormat(formatLocale, formatOptions);
};

/**
 * getCurrencyFormat
 *
 * Format numbers as currency
 *
 * @param currency The currency to use when formatting the value
 * @param locale A single locale or array of locales to use in finding the best match
 *
 * @example: en-US, USD, 987654321 => $987,654,321.00
 */
export const getCurrencyFormat = (
  currency = DEFAULT_CURRENCY,
  locale: LocaleType = DEFAULT_LOCALE,
) => {
  const formatLocale = getValidIntlLocale(locale, IntlFormat.NumberFormat);

  return new Intl.NumberFormat(formatLocale, { style: 'currency', currency });
};

/**
 * getCompactCurrencyFormat
 *
 *
 * Format numbers as currency using shorthand units
 *
 * @param currency The currency to use when formatting the value
 * @param locale A single locale or array of locales to use in finding the best match
 *
 * @example: en-US, USD, 987654321 => $987M
 */
export const getCompactCurrencyFormat = (
  currency = DEFAULT_CURRENCY,
  locale: LocaleType = DEFAULT_LOCALE,
) => {
  const formatLocale = getValidIntlLocale(locale, IntlFormat.NumberFormat);

  return new Intl.NumberFormat(formatLocale, { style: 'currency', currency, notation: 'compact' });
};

/**
 * getPercentageFormat
 *
 * Format numbers as percentage
 *
 * @param maximumFractionDigits Maximum number of decimal digits to show (i.e. setting this to 0 means no decimal value)
 * @param locale A single locale or array of locales to use in finding the best match
 *
 * @example: en-US, 0.45 => 45%
 * @example: en-US, 0.45837 => 45.84%
 */
export const getPercentFormat = (
  maximumFractionDigits = 2,
  locale: LocaleType = DEFAULT_LOCALE,
  options?: Intl.NumberFormatOptions,
) => {
  const formatLocale = getValidIntlLocale(locale, IntlFormat.NumberFormat);

  return new Intl.NumberFormat(formatLocale, {
    style: 'percent',
    maximumFractionDigits,
    ...options,
  });
};

/**
 * getCustomUnitFormat
 *
 * Formats a number using a given unit
 *
 * @param unit Unit of measurement to use. See [TC39]{@link https://tc39.es/proposal-unified-intl-numberformat/section6/locales-currencies-tz_proposed_out.html#sec-measurement-unit-identifiers} for available units
 * @param locale A single locale or array of locales to use in finding the best match
 *
 * @example en-US, short, mile-per-hour, 88 => 88 mph
 */
export const getCustomUnitFormat = (
  unit: string,
  unitDisplay: UnitDisplayType,
  locale: LocaleType = DEFAULT_LOCALE,
) => {
  const formatLocale = getValidIntlLocale(locale, IntlFormat.NumberFormat);

  return new Intl.NumberFormat(formatLocale, {
    style: 'unit',
    unit,
    unitDisplay,
    maximumFractionDigits: 2,
  });
};
